import { forwardRef } from "react"

import {
  FormControl,
  InputLabel,
  MenuItem,
  Radio,
  Select,
  SelectChangeEvent,
  Skeleton,
} from "@mui/material"
import { useTranslation } from "react-i18next"

import Resort from "@/queries/models/Resort"

type ResortSelectorProps = {
  resorts: Resort[] | undefined
  selectedResortExternalId: number
  setSelectedResort: (resortExternalId: number) => void
  isLoading?: boolean
}

const ResortSelector = forwardRef<HTMLDivElement, ResortSelectorProps>(
  (
    { resorts, selectedResortExternalId, setSelectedResort, isLoading = false },
    ref
  ) => {
    const { t } = useTranslation()
    const handleChange = (event: SelectChangeEvent<number>) => {
      const externalId = event.target.value
      const resort = resorts?.find((resort) => resort.externalId === externalId)
      if (resort) {
        setSelectedResort(resort.externalId)
      }
    }

    if (!resorts || isLoading) {
      return (
        <Skeleton
          data-testid="resort-selector-skeleton"
          variant="rectangular"
          width="100%"
          height={58}
        />
      )
    }

    return (
      <FormControl fullWidth>
        <InputLabel id="resort-select-label">
          {t("resortSelectLabel")}
        </InputLabel>
        <Select
          inputRef={ref}
          labelId="resort-select-label"
          id="resort-select"
          value={selectedResortExternalId}
          label={t("resortSelectLabel")}
          onChange={handleChange}
          MenuProps={{
            disableScrollLock: true,
            PaperProps: { "data-testid": "resort-selector" },
          }}
          renderValue={(value) =>
            resorts?.find((resort) => resort.externalId === value)?.name
          }
        >
          {resorts?.map((resort) => (
            <MenuItem key={resort.externalId} value={resort.externalId} divider>
              <Radio
                checked={selectedResortExternalId === resort.externalId}
                aria-hidden="true"
              />
              {resort.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    )
  }
)

ResortSelector.displayName = "ResortSelector"

export default ResortSelector
